<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/api';
import { useLodash } from '@/setup/lodash';
import { usePopupManager } from '@/components/PopupManager.vue';
import type {
  GetAffiliateOffersResponse,
  GetAffiliatesData,
  GetAffiliatesResponse, PutDomainsManagementPlatformBettingDomainsByIdData,
} from '@/types/openapi-ts';
import type { EmitsToListeners } from '@/types/core';
//#endregion

//#region types
type AffiliateType = {
  affiliate_email: string | null;
  affiliate_id: string;
};

interface Props {
  domainId: string,
  offer: string | null,
  affiliate: AffiliateType | null,
}

interface  Emits {
  'success': [],
  'close': [],
}

export interface PinAffiliateToDomainParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}
//#endregion

//#region define
const props = defineProps<Props>();
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const _ = useLodash();
const popupManager = usePopupManager();
const { t } = useI18n();
//#endregion

//#region state
const errors = ref({});
const isVisible = ref(false);
const isLoading = ref(false);
const selectLoading = ref(false);

const affiliateId = ref('');
const affiliateList = ref<GetAffiliatesResponse['payload']>([]);

const offerId = ref<string | null>(null);
const offerList = ref<GetAffiliateOffersResponse['payload']>([]);
//#endregion

//#region computed
const isEdit = computed(() => {
  return !!props.affiliate;
});
const isSaveDisabled = computed(() => {
  return (props.affiliate
    && props.affiliate.affiliate_id === affiliateId.value
    && props.offer === offerId.value)
  || !offerId.value;
});
//#endregion

//#region methods
async function open() {
  if (props.affiliate) {
    affiliateId.value = props.affiliate.affiliate_id;
    offerId.value = props.offer;

    affiliateList.value = [
      { id: props.affiliate.affiliate_id, email: props.affiliate.affiliate_email },
    ] as GetAffiliatesResponse['payload'];

    await getAffiliateOffers(affiliateId.value!);
  }

  await nextTick();
  isVisible.value = true;
}

function handleClose() {
  emit('close');
  isVisible.value = false;
}

function updatePlatformBettingDomain(value: string | null) {
  api.updatePlatformBettingDomain(props.domainId, {affiliate_offer_id: value}).then(() => {
    emit('success');
    isVisible.value = false;
  }).catch((error) => {
    errors.value = error.data.errors;
  }).finally(() => {
    isLoading.value = false;
  });
}

async function handleSave() {
  isLoading.value = true;
  errors.value = {};

  if(isEdit.value) {
    changePinAffiliate('attach');
  } else {
    updatePlatformBettingDomain(offerId.value);
  }
}

function changePinAffiliate(action: 'attach' | 'detach') {
  const popupParams = {
    attach: {
      title: t('players.confirm.action_1'),
      message: t('affiliates.confirm.msg_6_3'),
      bodyIconColor: 'var(--text-positive)',
      confirmTitle: t('players.confirm.action_1'),
      confirmIcon: 'link',
      confirmType: 'positive',
      successMethod: () => updatePlatformBettingDomain(offerId.value),
    },
    detach: {
      title: t('affiliates.affiliatesInfo.unpin'),
      message: t('affiliates.confirm.msg_6_2'),
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('players.confirm.action_2'),
      confirmIcon: 'unlink',
      confirmType: 'negative',
      successMethod: () => updatePlatformBettingDomain(null),
    },
  };

  const confirmPopup = popupManager.confirm.open({
    data: {
      title: popupParams[action]!.title,
      message: popupParams[action]!.message,
      confirmTitle: popupParams[action]!.confirmTitle,
      bodyIconColor: popupParams[action]!.bodyIconColor,
      confirmIcon: popupParams[action]!.confirmIcon,
      confirmType: popupParams[action]!.confirmType,
      bodyIcon: 'exclamation-triangle',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          popupParams[action].successMethod();
        } catch (error) {
          window.console.error(error);
        } finally {
          context.handleClose();
        }
      },
    },
  });
}

async function getAffiliateOffers(affiliateId: string) {
  const { data: { payload } } = await api.getAffiliateOffers(affiliateId);
  offerList.value = payload;
}

function changeAffiliate(affiliateId: string) {
  offerId.value = null;
  getAffiliateOffers(affiliateId);
}

function searchMethod(v: string) {
  selectLoading.value = true;
  _.debounce(getAffiliates, 800)(v);
}

function getAffiliates(search: string) {
  const query = {
    limit: 100,
    offset: 0,
    search,
    sort_column: 'email',
    sort_dir: 'asc',
  } as GetAffiliatesData['query'];
  return new Promise((resolve, reject) => {
    api.getAffiliates(query)
      .then((response) => {
        affiliateList.value = _.uniqBy([
          ...(props.affiliate && !search ?
              [{ id: props.affiliate.affiliate_id, email: props.affiliate.affiliate_email }] : []
          ),
          ...response.data.payload,
        ], 'id') as GetAffiliatesResponse['payload'];
        resolve(response.data.payload);
      })
      .catch((error) => {
        reject(error);
      })
      .finally(() => {
        selectLoading.value = false;
      });
  });
}
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="pin-affiliate-to-domain-popup"
    :title="$t('domainManagement.buttons.pin_domain')"
    :is-loading="isLoading"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <div class="t:space-x-8 t:flex t:items-end">
          <UiSelectElement
            v-model="affiliateId"
            :label="$t('players.pinPlayer.aff')"
            :placeholder="$t('players.pinPlayer.placeholder_aff')"
            :list="affiliateList"
            :remote-method="searchMethod"
            :loading="selectLoading"
            size="big"
            remote
            filterable
            option-label-param="email"
            option-name-param="email"
            @change="changeAffiliate"
          />
          <UiButton
            v-if="isEdit"
            :disabled="props.affiliate?.affiliate_id !== affiliateId"
            size="big"
            type="negative"
            icon="unlink"
            @click="changePinAffiliate('detach')"
          >
            {{ $t('players.pinPlayer.unpin') }}
          </UiButton>
        </div>
        <UiSelectElement
          v-model="offerId"
          :label="$t('players.pinPlayer.offer')"
          :placeholder="$t('players.pinPlayer.placeholder_offer')"
          :list="offerList"
          :disabled="!affiliateId"
          size="big"
          filterable
          option-label-param="full_name"
          option-name-param="full_name"
        />
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          :disabled="isLoading"
          @click="handleClose"
        >
          {{ $t(`domainManagement.popup.cancel`) }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          :disabled="isSaveDisabled || isLoading"
          @click="handleSave"
        >
          {{ $t('domainManagement.popup.allocate') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
